/**
 * Copy-DATA serialization: the artifact's underlying VALUES as plain text (never a link). Per kind:
 *   table/plot → delegated to ai-teammate's `artifactToDownloadData`, joined as TSV. Plot with no x/labels falls back to JSON of the trace data.
 *   text       → the text verbatim
 *   markdown   → the raw markdown verbatim
 *   html       → the raw HTML string verbatim
 *   unknown    → pretty-printed JSON of the payload (id/type/title stripped)
 */

import { artifactToDownloadData as artifactToDownloadDataImpl } from "connected-components/ai-teammate/helpers/artifact-to-download-data"
import type { DownloadCell, DownloadData } from "connected-components/dashboard/context/download-registry"
import {
  type ArtifactEnvelope,
  type PlotArtifact,
  type TableArtifact,
  isHtmlArtifact,
  isMarkdownArtifact,
  isPlotArtifact,
  isTableArtifact,
  isTextArtifact,
} from "./artifact-contract"

export type { DownloadData }

// Typed view over the canonical exporter (same untyped-boundary pattern as the renderer's component views): it
// reads exactly the fields our guards already validated (table columns/records, plot figure.data incl. pie
// labels/values). Kept as a view rather than re-narrowing to ai-teammate's Artifact union to avoid forking types.
export const artifactToDownloadData = artifactToDownloadDataImpl as (artifact: TableArtifact | PlotArtifact) => DownloadData | null

export function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return "[artifact data could not be serialized]"
  }
}

export function cellToText(cell: DownloadCell): string {
  return typeof cell === "object" ? String(cell.v) : String(cell)
}

function serializeDownloadData({ headers, rows }: DownloadData): string {
  return [headers, ...rows.map((row) => row.map(cellToText))].map((row) => row.join("\t")).join("\n")
}

const ENVELOPE_METADATA_KEYS = new Set(["id", "type", "title", "name"])

export function serializePayloadJson(artifact: ArtifactEnvelope): string {
  const payload = Object.fromEntries(
    Object.entries(artifact).filter(([key]) => !ENVELOPE_METADATA_KEYS.has(key)),
  )
  return safeJson(payload)
}

/** The clipboard payload for an artifact's Copy-DATA action. */
export function serializeArtifactData(artifact: ArtifactEnvelope): string {
  if (isTableArtifact(artifact)) {
    const data = artifactToDownloadData(artifact)
    return data ? serializeDownloadData(data) : ""
  }
  if (isPlotArtifact(artifact)) {
    const data = artifactToDownloadData(artifact)
    return data ? serializeDownloadData(data) : safeJson(artifact.figure.data)
  }
  if (isTextArtifact(artifact)) return artifact.text
  if (isMarkdownArtifact(artifact)) return artifact.markdown
  if (isHtmlArtifact(artifact)) return artifact.html
  return serializePayloadJson(artifact)
}
